import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { api, apiErr } from "../lib/api";
import { COMMERCIAL_SERVICES, FACILITY_TYPES, FREQUENCIES, IMAGES } from "../lib/constants";
import { PageHero, SectionHeading, fadeUp, ServiceAreaSection } from "../components/common";

const SERVICE_OPTIONS = COMMERCIAL_SERVICES.map((s) => s.title);
const CONTACT_METHODS = ["Phone", "Email", "Text"];

const empty = {
  name: "", company_name: "", phone: "", email: "", property_address: "",
  facility_type: "", square_footage: "", frequency: "", services_needed: [],
  preferred_contact: "", details: "", website: "",
};

export default function Commercial() {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (s) =>
    setForm((f) => ({ ...f, services_needed: f.services_needed.includes(s)
      ? f.services_needed.filter((x) => x !== s) : [...f.services_needed, s] }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email) { toast.error("Please complete name, phone and email."); return; }
    setSending(true);
    try {
      await api.post("/commercial-leads", form);
      setDone(true);
      toast.success("Quote request received! We'll be in touch shortly.");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(apiErr(e)); }
    setSending(false);
  };

  const inputCls = "w-full border border-gray-300 rounded-md px-4 py-2.5 focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-colors";
  const labelCls = "block text-sm font-semibold text-charcoal-700 mb-1.5";

  return (
    <div data-testid="commercial-page">
      <PageHero eyebrow="Commercial Cleaning & Building Maintenance"
        title="Commercial janitorial services built around your facility"
        subtitle="Professional, consistent cleaning for offices, medical facilities, churches, retail spaces, property management, and more across Northwest Arkansas."
        image={IMAGES.officeBuilding}>
        <Link to="#quote" className="inline-flex items-center gap-2 bg-primary text-white hover:bg-primary-hover font-semibold px-7 py-3.5 rounded-md transition-colors" data-testid="hero-quote-btn">
          Request a Commercial Cleaning Quote <ArrowRight size={18} />
        </Link>
      </PageHero>

      <section className="bg-white">
        <div className="ubm-container py-20">
          <SectionHeading eyebrow="Our services" title="Comprehensive commercial cleaning"
            subtitle="A full range of janitorial and building maintenance services, available on recurring plans or as one-time projects." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {COMMERCIAL_SERVICES.map((s, i) => (
              <motion.div key={s.title} {...fadeUp} transition={{ delay: (i % 3) * 0.05 }}
                className="border border-gray-200 rounded-md p-6 hover:border-primary hover:shadow-md transition-all">
                <CheckCircle2 className="text-primary mb-3" size={22} />
                <h3 className="font-semibold text-charcoal-900 text-lg">{s.title}</h3>
                <p className="mt-1.5 text-sm text-charcoal-600 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
          <p className="mt-8 text-sm text-charcoal-500">
            Commercial cleaning uses a quote-based system — no instant online payment. Tell us about your property and we'll build a custom plan and pricing.
          </p>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section id="quote" className="bg-[#f8f9fa] scroll-mt-24" data-testid="commercial-quote">
        <div className="ubm-container py-20">
          <div className="grid lg:grid-cols-5 gap-10">
            <div className="lg:col-span-2">
              <SectionHeading eyebrow="Request a quote" title="Tell us about your facility"
                subtitle="Share a few details and our team will follow up with a tailored commercial cleaning proposal." />
              <img src={IMAGES.commercialTeam} alt="Cleaning team" className="rounded-md border border-gray-200 w-full h-64 object-cover" />
            </div>

            <div className="lg:col-span-3">
              {done ? (
                <div className="bg-white border border-primary/30 rounded-md p-10 text-center" data-testid="quote-success">
                  <CheckCircle2 className="text-primary mx-auto mb-4" size={48} />
                  <h3 className="text-2xl font-bold text-charcoal-900">Thank you!</h3>
                  <p className="mt-2 text-charcoal-600">Your commercial quote request has been received. We'll contact you soon via your preferred method.</p>
                  <button onClick={() => { setForm(empty); setDone(false); }} className="mt-6 border-2 border-charcoal-900 text-charcoal-900 hover:bg-charcoal-900 hover:text-white font-semibold px-6 py-2.5 rounded-md transition-colors">
                    Submit another request
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="bg-white border border-gray-200 rounded-md p-6 sm:p-8 space-y-5" data-testid="quote-form">
                  <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => set("website", e.target.value)} className="hidden" aria-hidden />
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><label className={labelCls}>Name *</label><input className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="q-name" required /></div>
                    <div><label className={labelCls}>Company Name</label><input className={inputCls} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="q-company" /></div>
                    <div><label className={labelCls}>Phone Number *</label><input className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="q-phone" required /></div>
                    <div><label className={labelCls}>Email *</label><input type="email" className={inputCls} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="q-email" required /></div>
                  </div>
                  <div><label className={labelCls}>Property Address</label><input className={inputCls} value={form.property_address} onChange={(e) => set("property_address", e.target.value)} data-testid="q-address" /></div>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div><label className={labelCls}>Type of Facility</label>
                      <select className={inputCls} value={form.facility_type} onChange={(e) => set("facility_type", e.target.value)} data-testid="q-facility">
                        <option value="">Select…</option>{FACILITY_TYPES.map((f) => <option key={f}>{f}</option>)}
                      </select>
                    </div>
                    <div><label className={labelCls}>Approx. Sq Footage</label><input className={inputCls} value={form.square_footage} onChange={(e) => set("square_footage", e.target.value)} data-testid="q-sqft" /></div>
                    <div><label className={labelCls}>Cleaning Frequency</label>
                      <select className={inputCls} value={form.frequency} onChange={(e) => set("frequency", e.target.value)} data-testid="q-frequency">
                        <option value="">Select…</option>{FREQUENCIES.map((f) => <option key={f}>{f}</option>)}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Services Needed</label>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {SERVICE_OPTIONS.map((s) => (
                        <label key={s} className="flex items-center gap-2 text-sm text-charcoal-700 cursor-pointer">
                          <input type="checkbox" checked={form.services_needed.includes(s)} onChange={() => toggleService(s)} className="accent-[#22b455] w-4 h-4" />
                          {s}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div><label className={labelCls}>Preferred Contact Method</label>
                    <select className={inputCls} value={form.preferred_contact} onChange={(e) => set("preferred_contact", e.target.value)} data-testid="q-contact-method">
                      <option value="">Select…</option>{CONTACT_METHODS.map((c) => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div><label className={labelCls}>Additional Details</label><textarea rows={4} className={inputCls} value={form.details} onChange={(e) => set("details", e.target.value)} data-testid="q-details" /></div>
                  <button type="submit" disabled={sending} className="w-full bg-primary text-white hover:bg-primary-hover font-semibold px-6 py-3.5 rounded-md transition-colors disabled:opacity-60" data-testid="q-submit">
                    {sending ? "Sending…" : "Request My Quote"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <ServiceAreaSection />
    </div>
  );
}
